import { useEffect, useState } from 'react';
import { PaymentMethod, type PaymentInformationData } from '../../../../types';
import type { PaymentFormProps, PaymentFormState, PaymentFormErrors } from '../types/payment-form.types';
import { validatePaymentForm } from '../utils/payment-validation';

declare global {
  interface Window {
    __pagouCardReady?: boolean;
  }
}

export const usePaymentForm = (props: PaymentFormProps) => {
  const { paymentData, onChangePaymentMethod } = props;
  const allowedMethods = new Set((props.availablePaymentMethods || []).map((method) => String(method).toLowerCase()));
  const hasAvailableMethods = allowedMethods.size > 0;

  const isMethodAllowed = (method?: PaymentMethod | ''): method is PaymentMethod => {
    if (!method) return false;
    if (!hasAvailableMethods) return true;
    return allowedMethods.has(String(method).toLowerCase());
  };

  const getDefaultMethod = (): PaymentMethod | '' => {
    if (isMethodAllowed(paymentData?.paymentMethod)) {
      return paymentData.paymentMethod;
    }

    // Preferência configurada no checkout (`config.steps.payment.preferredPaymentMethod`),
    // respeitada só quando o método ainda está disponível para esta oferta.
    const preferred = props.preferredPaymentMethod as PaymentMethod | undefined;
    if (preferred && isMethodAllowed(preferred)) {
      return preferred;
    }

    if (!hasAvailableMethods) {
      return PaymentMethod.CREDIT_CARD;
    }

    return [PaymentMethod.CREDIT_CARD, PaymentMethod.PIX, PaymentMethod.BOLETO].find(isMethodAllowed) ?? '';
  };

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | ''>(
    getDefaultMethod()
  );

  const [creditCardData, setCreditCardData] = useState(paymentData?.creditCard || {
    cardNumber: '', 
    expirationDate: '', 
    cvv: '', 
    cardholderName: '', 
    cpf: '', 
    installments: '1',
  });

  // ✅ Inicialização corrigida - garante que o CPF seja copiado corretamente
  const [pixData, setPixData] = useState(() => {
    const initialPix: { cpf: string } = { cpf: '' };
    if (paymentData?.pix?.cpf) {
      initialPix.cpf = paymentData.pix.cpf;
    } else if (paymentData?.creditCard?.cpf) {
      initialPix.cpf = paymentData.creditCard.cpf;
    }
    return initialPix;
  });
  
  const [boletoData, setBoletoData] = useState(() => {
    const initialBoleto: { cpf: string } = { cpf: '' };
    if (paymentData?.boleto?.cpf) {
      initialBoleto.cpf = paymentData.boleto.cpf;
    } else if (paymentData?.creditCard?.cpf) {
      initialBoleto.cpf = paymentData.creditCard.cpf;
    }
    return initialBoleto;
  });
  
  const [errors, setErrors] = useState<PaymentFormErrors>({});


  // Reset errors when payment method changes
  useEffect(() => {
    setErrors({});
  }, [selectedMethod]);

  useEffect(() => {
    if (!hasAvailableMethods) return;
    if (isMethodAllowed(selectedMethod)) return;

    const nextMethod = getDefaultMethod();
    if (nextMethod !== selectedMethod) {
      setSelectedMethod(nextMethod);
    }
  }, [selectedMethod, paymentData?.paymentMethod, hasAvailableMethods, props.availablePaymentMethods]);

  // ✅ Sincroniza pixData e boletoData quando paymentData mudar
  useEffect(() => {
    if (paymentData?.pix?.cpf && !pixData.cpf) {
      setPixData(prev => ({ ...prev, cpf: paymentData.pix!.cpf }));
    }
    
    if (paymentData?.boleto?.cpf && !boletoData.cpf) {
      setBoletoData(prev => ({ ...prev, cpf: paymentData.boleto!.cpf }));
    }
  }, [paymentData, pixData.cpf, boletoData.cpf]);

  // Atualiza preços quando entra na seção de pagamento ou muda método
  useEffect(() => {
    
    if (selectedMethod && onChangePaymentMethod) {
      const paymentInformation: PaymentInformationData = {
        paymentMethod: selectedMethod,
        creditCard: creditCardData,
        pix: pixData,
        boleto: boletoData,
      };
      onChangePaymentMethod(paymentInformation);
    }
  }, [selectedMethod]); // Executa apenas quando o método de pagamento muda

  const handleMethodChange = (method: PaymentMethod) => {
    setSelectedMethod(method);
    // onChangePaymentMethod é chamado pelo useEffect acima quando selectedMethod muda
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, method: PaymentMethod | '') => {
    const { name, value } = e.target;
    const currentErrors = { ...errors };
    if (currentErrors[name as keyof PaymentFormErrors]) {
      delete currentErrors[name as keyof PaymentFormErrors];
    }
    setErrors(currentErrors);

    switch (method) {
      case PaymentMethod.CREDIT_CARD:
        setCreditCardData(prev => ({ ...prev, [name]: value }));
        break;
      case PaymentMethod.PIX:
        setPixData(prev => ({ ...prev, [name]: value }));
        break;
      case PaymentMethod.BOLETO:
        setBoletoData(prev => ({ ...prev, [name]: value }));
        break;
    }
  };

  const useStripeElements = props.creditCardPrimaryGateway === 'stripe' && !!props.stripePublishableKey;
  const usePagouElements = props.creditCardPrimaryGateway === 'pagou' && !!props.pagouPublicKey;

  const validate = (): boolean => {
    const state: PaymentFormState = {
      selectedMethod,
      creditCardData,
      pixData,
      boletoData,
      errors,
    };
    const newErrors = validatePaymentForm(state, {
      useStripeElements,
      usePagouElements,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Bloqueia Finalizar enquanto o iframe Pagou ainda carrega (evita race __pagouSubmitToken).
    if (
      selectedMethod === PaymentMethod.CREDIT_CARD &&
      usePagouElements &&
      typeof window !== 'undefined' &&
      window.__pagouCardReady !== true
    ) {
      setErrors((prev) => ({
        ...prev,
        cardNumber: 'Aguarde o checkout seguro carregar antes de finalizar.',
      }));
      return;
    }
    
    if (validate()) {
      const paymentInfoToSave: PaymentInformationData = { 
        paymentMethod: selectedMethod,
      };
      
      if (selectedMethod === PaymentMethod.CREDIT_CARD) {
        paymentInfoToSave.creditCard = creditCardData;
      }
      if (selectedMethod === PaymentMethod.PIX) {
        paymentInfoToSave.pix = pixData;
      }
      if (selectedMethod === PaymentMethod.BOLETO) {
        paymentInfoToSave.boleto = boletoData;
      }
      
      
      // Salva os dados primeiro
      props.onSave(paymentInfoToSave);
      
      // Chama onPlaceOrder automaticamente após salvar (apenas uma vez)
      if (props.onPlaceOrder) {
        props.onPlaceOrder();
      }
    }
  };

  return {
    selectedMethod,
    creditCardData,
    pixData,
    boletoData,
    errors,
    handleMethodChange,
    handleChange,
    handleSubmit,
  };
}; 